import {Injectable, OnDestroy} from '@angular/core';
import {Subject, ReplaySubject} from 'rxjs';
import {TrustiiService} from './trustii.service';
import {of} from 'rxjs';
import {tap, catchError} from 'rxjs/operators';
import {Router} from '@angular/router';

export enum UserType {
  Enterprise = 'enterprise',
  Vendor = 'vendor'
}

@Injectable({
  providedIn: 'root'
})
export class SessionService implements OnDestroy {
  private userType: UserType = null;
  private token = null;
  private user = {
    id: null,
    firstname: null,
    lastname: null,
    email: null,
    vendor: null,
    roleId: null,
  };
  private vendor;
  userUpdate = new ReplaySubject();
  currentUrl: string = null;

  constructor(private trustiiService: TrustiiService, private router: Router) {
    this.start();
  }

  tokenData = new ReplaySubject();
  storageData = new Subject();

  setUrl(url: string) {
    this.currentUrl = url;
  }

  getUrl() {
    return this.currentUrl;
  }

  setUserType(userType: UserType) {
    this.userType = userType;
    this.userUpdate.next();
  }

  getUserType() {
    return this.userType;
  }

  setToken(value) {
    this.token = value;
  }

  getToken() {
    return this.token;
  }

  setUser(options) {
    this.user.id = options.id;
    this.user.firstname = options.firstname;
    this.user.lastname = options.lastname;
    this.user.email = options.email;
    this.user.roleId = options.roleId;
    this.user.vendor = options.vendor;
    this.vendor = options.vendor;
    this.setUserType(UserType.Vendor);
    this.userUpdate.next();
  }

  getVendor() {
    return this.vendor;
  }

  getVendorPlan() {
    return (this.vendor) ? this.vendor.planId : 0;
  }

  getVendorMonthlyStats() {
    return this.vendor.monthlyStats;
  }

  getUser() {
    return this.user;
  }

  isVendorUser() {
    return UserType.Vendor === this.userType;
  }

  isFreePlan() {
    return this.vendor && this.vendor.planId === 1;
  }

  refreshUser() {
    this.refreshUserObervable().subscribe();
  }

  refreshUserObervable(): any {
    return this.trustiiService.autoLogin().pipe(
      tap(data => this.setUser(data.data)),
      catchError(err => of(null))
    );
  }

  /**
   * PLAN MANAGEMENT
   */

  canAddNewUser(datasetSize: number = 0) {
    if (!this.vendor || this.vendor.planId === 1) {
      return false;
    }

    if (this.vendor.planId === 3) {
      return true;
    }

    if (this.vendor.nbActiveUsers < 3) {
      return true;
    }

    return false;
  }

  canUploadDataset(datasetSize: number = 0) {
    if (this.vendor && this.vendor.planId !== 1) {
      return true;
    }

    const totalSize = this.getVendorMonthlyStats().cumulativeDatasetSize + datasetSize;
    if (totalSize > 100000000) {
      return false;
    }

    return true;
  }

  willReachDatasetLimit(datasetSize: number = 0) {
    const totalSize = this.getVendorMonthlyStats().cumulativeDatasetSize + datasetSize;
    if (this.vendor.planId === 1 && totalSize > 100000000) {
      return true;
    }

    if (this.vendor.planId === 2 && totalSize > 100000000) {
      return true;
    }

    if (this.vendor.planId === 3 && totalSize > 100000000) {
      return true;
    }

    if (this.vendor.planId === 4 && totalSize > 1000000000) {
      return true;
    }
    return false;
  }

  canGenerateAutoModel(processingTime: number = 0) {
    if (this.vendor.planId !== 1) {
      return true;
    }

    const totalSize = this.getVendorMonthlyStats().autoModelProcessingTime + processingTime;
    if (totalSize > 10) {
      return false;
    }

    return true;
  }

  willReachAutoModelLimit(processingTime: number = 0) {
    const totalSize = this.getVendorMonthlyStats().autoModelProcessingTime + processingTime;
    if (this.vendor.plan === 1 && totalSize > 10) {
      return true;
    }

    if (this.vendor.plan === 2 && totalSize > 90) {
      return true;
    }

    if (this.vendor.plan === 3 && totalSize > 240) {
      return true;
    }

    return false;
  }

  canAccessChallengeFunctionnality() {
    if (this.vendor.planId === 1 || this.vendor.planId === 2 || this.vendor.planId === 3) {
      return false;
    }
    return true;
  }

  canCreateChallenge() {
    if (this.vendor.planId === 1 || this.vendor.planId === 2 || this.vendor.planId === 3) {
      return false;
    }

    const nbChallenges = this.vendor.yearlyStats.nbChallenges;

    // @todo Manage oneTime plan
    if (nbChallenges >= 4) {
      return false;
    }

    return true;
  }

  isFreeBadge(): boolean {
    return this.vendor.planId === 1;
  }

  isDsBadge(): boolean {
    return this.vendor.planId === 2;
  }

  isTeamBadge(): boolean {
    return this.vendor.planId === 3;
  }

  isEnterpriseBadge(): boolean {
    return this.vendor.planId === 4;
  }

  canGenerateInsight(insightSize: number = 0) {
    if (this.vendor.planId !== 1) {
      return true;
    }

    const totalSize = this.getVendorMonthlyStats().cumulativePredictionSize + insightSize;
    if (totalSize > 20000000) {
      return false;
    }

    return true;
  }

  willReachInsightLimit(insightSize: number = 0) {
    const totalSize = this.getVendorMonthlyStats().cumulativePredictionSize + insightSize;
    if ((this.vendor.planId === 1 || this.vendor.planId === 2) && totalSize > 20000000) {
      return true;
    }

    if (this.vendor.planId === 3 && totalSize > 20000000) {
      return true;
    }

    if (this.vendor.planId === 4 && totalSize > 1000000000) {
      return true;
    }

    return false;
  }

  /**
   * PLAN MANAGEMENT
   */

  ngOnDestroy() {
    this.stop();
  }

  private start(): void {
    window.addEventListener('storage', this.storageEventListener.bind(this));
  }

  private storageEventListener(event: StorageEvent) {
    this.storageData.next();
  }

  private stop(): void {
    window.removeEventListener('storage', this.storageEventListener.bind(this));
    this.storageData.complete();
    this.tokenData.complete();
  }
}
